/* hs-eslint ignored failing-rules */

/* eslint-disable promise/catch-or-return */
'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { flatten, mapObject, omit } from 'underscore';
import { byName as TemplateTypes } from 'ContentUtils/constants/TemplateTypes';
import { fetchCustomWidgetRevisions, fetchGlobalWidgetRevisions, fetchLayoutRevisions, fetchTemplateRevisions, fetchRawAssetRevisions } from 'DesignData/actions/Revisions';
import { revertCustomWidget } from 'DesignData/actions/CustomWidgetBuffers';
import { revert as revertGlobalWidget } from 'DesignData/actions/GlobalWidgets';
import { revert as revertTemplateBuffers } from 'DesignData/actions/TemplateBuffers';
import { revertLayout as revertUnifiedLayout } from 'DesignData/actions/UnifiedLayouts';
import { revertRawAsset } from 'DesignData/actions/RawAssetBuffers';
import { create as finderFilesCreate, del as finderFilesDelete, bulkDelete as finderFilesBulkDelete, clone as finderFilesClone, cloneByPath as finderFilesCloneByPath, restoreDeleted as finderFilesRestoreDeleted, save as finderFilesSave, saveByPath as finderFilesSaveByPath, createByPath as finderFilesCreateByPath } from 'DesignData/api/finder/Files';
import { cloneToFile as layoutCloneToFile } from 'DesignData/api/Layouts';
import { byModuleId as customWidgetsByModuleId } from 'DesignData/api/CustomWidgets';
import { CSS as DEFAULT_FILE_SOURCE_CSS } from 'DesignData/constants/DefaultFileSources';
import FileModel from 'DesignData/models/finder/File';
import { getRootFolderId, getFile, getFolder, getDuplicateFileName, getDuplicateFileFolderId } from 'DesignData/selectors/finder';
import { getCustomWidgetsData } from 'DesignData/selectors/customWidgets';
import { getIsModuleIdMigrationUngated } from 'DesignData/selectors/gates';
import { getHasGlobalContentEditorAccess } from 'DesignData/selectors/auth';
import { getGlobalWidgetNameFromLabel } from 'DesignData/helpers/globalWidgets';
import { copyToClipboard, getModuleSnippet } from 'DesignData/lib/copySnippets';
import { convertToAbsolute } from 'DesignData/lib/path';
import { HOLLOW } from 'DesignData/enums/AssetSources';
import { fetchContents } from 'DesignData/actions/finder/Folders';
import { uploadFile } from 'DesignData/api/FileUpload';
import * as FolderAssetTypes from 'DesignData/enums/FolderAssetTypes';
import { MODULE_ASSET_EXTENSION } from 'DesignData/constants/CustomWidgets';
import { FINDER_FILE_CREATE, FINDER_FILE_CREATE_FROM_EDITOR, FINDER_FILE_DELETE, FINDER_FILE_DUPLICATE, FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE, FINDER_FILE_DUPLICATE_READ_ONLY, FINDER_FILE_HIDE_DEPENDENTS, FINDER_FILE_SHOW_DEPENDENTS, FINDER_FILE_RENAME, FINDER_FILE_RESTORE_DELETED, FINDER_FILE_SHOW_REVISIONS, FINDER_FILE_HIDE_REVISIONS, FINDER_FILE_UPLOAD, FINDER_FILE_UPLOAD_FINISHED, FINDER_FILE_UPLOAD_UPDATE_PROGRESS, FINDER_FILES_BULK_DELETE, FINDER_FILE_CREATE_PAGE, FINDER_FILE_COPY_PATH, FINDER_FILE_COPY_PUBLIC_URL, FINDER_FILE_COPY_MODULE_SNIPPET, FINDER_FILE_VIEW_LAYOUT_SOURCE } from 'DesignData/actions/ActionTypes';
import { validateFilenameCharacters } from 'DesignData/lib/validation';
import { RAW_ASSET } from 'DesignData/enums/FolderAssetTypes';
import { FILENAME_CONTAINS_INVALID_CHAR } from 'DesignData/enums/FileUploadErrorTypes';
/*
 * POST
 *  - designmanager/v1/layouts
 *  - designmanager/v1/templates
 *  - designmanager/v1/custom-widgets
 */

export function createFile(props) {
  const {
    request,
    receive,
    error
  } = createFile;
  return (dispatch, getState) => {
    const {
      fileType,
      fileName,
      folderId: __folderId
    } = props,
          rest = _objectWithoutPropertiesLoose(props, ["fileType", "fileName", "folderId"]);

    const state = getState();

    const folderId = __folderId || getRootFolderId(state);

    const folder = getFolder(state, {
      folderId
    });

    if (!folder) {
      const referenceError = new ReferenceError(`Unable to find folder with ID '${folderId}.`);
      return dispatch(error(referenceError, props));
    }

    let requestData = {
      folderId
    };
    let path;

    switch (fileType) {
      case FolderAssetTypes.LAYOUT:
      case FolderAssetTypes.TEMPLATE:
        {
          const {
            templateType
          } = rest;
          requestData = Object.assign({}, requestData, {
            label: fileName,
            templateType
          });

          if (fileType === FolderAssetTypes.TEMPLATE) {
            const {
              templatePartialType,
              isAvailableForNewContent
            } = rest;
            requestData = Object.assign({}, requestData, {
              isAvailableForNewContent,
              templateType: getHasGlobalContentEditorAccess(state) ? isAvailableForNewContent ? templateType : templatePartialType : templateType
            });

            if (!/\.html$/i.test(fileName)) {
              requestData.label = `${requestData.label}.html`;
            }

            path = `${folder.fullPath}/${requestData.label}`;
          }

          break;
        }

      case FolderAssetTypes.CUSTOM_WIDGET:
        {
          const {
            contentTypes,
            hostTemplateTypes,
            isGlobal
          } = rest;
          path = `${folder.fullPath}/${fileName}`;
          requestData = Object.assign({}, requestData, {
            contentTypes,
            hostTemplateTypes,
            name: fileName,
            fields: [],
            schemaVersion: 2,
            source: '',
            writeable: true,
            global: isGlobal,
            path
          });
          break;
        }

      case FolderAssetTypes.CSS:
      case FolderAssetTypes.JS:
        {
          const expectedFileExtension = fileType === FolderAssetTypes.CSS ? 'css' : 'js';
          const fileExtensionRegex = new RegExp(`.${expectedFileExtension}$`, 'i');
          requestData = Object.assign({}, requestData, {
            label: fileName,
            isAvailableForNewContent: false
          });

          if (fileType === FolderAssetTypes.CSS) {
            requestData = Object.assign({}, requestData, {
              templateType: TemplateTypes.css.id,
              source: DEFAULT_FILE_SOURCE_CSS
            });
          } else {
            requestData = Object.assign({}, requestData, {
              templateType: TemplateTypes.js.id,
              source: ''
            });
          }

          if (!fileExtensionRegex.test(fileName)) {
            requestData.label = `${requestData.label}.${expectedFileExtension}`;
          }

          break;
        }

      default:
        break;
    }

    dispatch(request(props));

    const onCreateSuccess = result => {
      dispatch(fetchContents(result.folderId));
      dispatch(receive(result, fileType));
    };

    const onCreateError = __error => {
      dispatch(error(__error, props));
    };

    if (path) {
      return finderFilesCreateByPath(fileType, path, requestData).then(onCreateSuccess).catch(onCreateError);
    } else {
      return finderFilesCreate(fileType, requestData).then(onCreateSuccess).catch(onCreateError);
    }
  };
}

createFile.request = props => {
  return Object.assign({}, props, {
    type: FINDER_FILE_CREATE
  });
};

createFile.receive = (file, fileType, openNewTab = true) => {
  return {
    type: FINDER_FILE_CREATE,
    file,
    fileType,
    openNewTab
  };
}; // TODO - Error message handling


createFile.error = (error, props) => {
  return Object.assign({}, props, {
    type: FINDER_FILE_CREATE,
    error
  });
};
/**
 * Create finder files when asset is fetched from editor
 * @param {Object} file Raw asset fetched from editor.
 * @param {FolderAssetType} fileType
 */


export function createFileFromEditor(file, fileType) {
  return {
    type: FINDER_FILE_CREATE_FROM_EDITOR,
    file,
    fileType
  };
}
/*
 * DELETE
 *  - designmanager/v1/layouts
 *  - designmanager/v1/templates
 *  - designmanager/v1/modules/by-module-id
 */

export function deleteFile(fileId, __folderId) {
  const {
    request,
    receive,
    error
  } = deleteFile;
  return (dispatch, getState) => {
    const state = getState();

    const folderId = __folderId || getRootFolderId(state);

    const file = getFile(state, {
      fileId
    });

    if (!file) {
      return dispatch(error(new ReferenceError('Unable to find file to delete')));
    }

    const {
      type
    } = file;
    const designAssetType = FileModel.getDesignAssetType(type);
    dispatch(request(fileId));
    const useByModuleIdClient = getIsModuleIdMigrationUngated(state) && file.type === FolderAssetTypes.CUSTOM_WIDGET;

    if (useByModuleIdClient && !file.moduleId) {
      return dispatch(error(fileId, new ReferenceError(`deleteFile: Missing moduleId for file fileId = '${fileId}'`)));
    }

    const req = useByModuleIdClient ? customWidgetsByModuleId.del(file.moduleId) : finderFilesDelete(type, fileId);
    return req.then(() => {
      dispatch(receive(fileId, designAssetType, folderId, type));
    }).catch(__error => {
      dispatch(error(fileId, __error));
    });
  };
}

deleteFile.request = fileId => {
  return {
    type: FINDER_FILE_DELETE,
    fileId
  };
};

deleteFile.receive = (fileId, fileAssetType, folderId, type) => {
  return {
    type: FINDER_FILE_DELETE,
    fileId,
    fileAssetType,
    folderId,
    fileType: type,
    deleted: true
  };
}; // TODO - Error message handling


deleteFile.error = (fileId, error) => {
  return {
    type: FINDER_FILE_DELETE,
    fileId,
    error
  };
};
/**
 * Bulk delete files
 * @param {Array.<Number>} fileIds
 *
 * DELETE
 *  - designmanager/v1/layouts/bulk
 *  - designmanager/v1/templates/bulk
 *  - designmanager/v1/modules/by-module-id/bulk
 */


export function bulkDeleteFiles(fileIds) {
  const {
    request,
    receive,
    error
  } = bulkDeleteFiles;
  return (dispatch, getState) => {
    const state = getState();
    const fileAssetTypes = omit(FolderAssetTypes, FolderAssetTypes.FOLDER);
    const fileIdsByType = mapObject(fileAssetTypes, () => []);
    const moduleIds = [];
    const isModuleIdMigrationUngated = getIsModuleIdMigrationUngated(state);
    dispatch(request(fileIds));
    fileIds.forEach(fileId => {
      const file = getFile(state, {
        fileId
      });
      if (!file) return;
      fileIdsByType[file.type].push(fileId);

      if (isModuleIdMigrationUngated && file.type === FolderAssetTypes.CUSTOM_WIDGET && file.moduleId) {
        moduleIds.push(file.moduleId);
      }
    });
    const requests = Object.keys(fileIdsByType).reduce((reqs, fileType) => {
      const fileTypeIds = fileIdsByType[fileType];
      if (!fileTypeIds.length) return reqs;

      if (isModuleIdMigrationUngated) {
        if (fileType !== FolderAssetTypes.CUSTOM_WIDGET) {
          reqs.push(finderFilesBulkDelete(fileType, fileTypeIds));
        }
      } else {
        reqs.push(finderFilesBulkDelete(fileType, fileTypeIds));
      }

      return reqs;
    }, []);

    if (moduleIds.length) {
      requests.push(customWidgetsByModuleId.bulkDelete(moduleIds));
    }

    Promise.all(requests).then(results => {
      dispatch(receive(flatten(results), fileIds, fileIdsByType));
    }).catch(__error => {
      dispatch(error(__error, fileIds));
    });
  };
}

bulkDeleteFiles.request = fileIds => {
  return {
    type: FINDER_FILES_BULK_DELETE,
    fileIds
  };
};

bulkDeleteFiles.receive = (results, fileIds, fileIdsByType) => {
  return {
    type: FINDER_FILES_BULK_DELETE,
    fileIds,
    results,
    fileIdsByType
  };
};

bulkDeleteFiles.error = (error, fileIds) => {
  return {
    type: FINDER_FILES_BULK_DELETE,
    fileIds,
    error
  };
};
/*
  Toggle file dependents
 */


export function showDependents(fileId) {
  return {
    type: FINDER_FILE_SHOW_DEPENDENTS,
    fileId
  };
}
export function hideDependents(fileId) {
  return {
    type: FINDER_FILE_HIDE_DEPENDENTS,
    fileId
  };
}
/*
  Toggle file revisions
 */

export function showRevisions(fileId) {
  return {
    type: FINDER_FILE_SHOW_REVISIONS,
    fileId
  };
}
export function hideRevisions(fileId) {
  return {
    type: FINDER_FILE_HIDE_REVISIONS,
    fileId
  };
}
/**
 * Fetch file revisions.
 * @param  {Number} fileId ID of file to fetch revisions for.
 */

export function fetchRevisions(fileId, offset) {
  return (dispatch, getState) => {
    const state = getState();
    const {
      type: fileType
    } = getFile(state, {
      fileId
    });
    let fetchRevisionsAction;

    switch (fileType) {
      case FolderAssetTypes.TEMPLATE:
      case FolderAssetTypes.CSS:
      case FolderAssetTypes.JS:
        {
          fetchRevisionsAction = fetchTemplateRevisions;
          break;
        }

      case FolderAssetTypes.LAYOUT:
      case FolderAssetTypes.GLOBAL_GROUP:
        {
          fetchRevisionsAction = fetchLayoutRevisions;
          break;
        }

      case FolderAssetTypes.GLOBAL_WIDGET:
        {
          fetchRevisionsAction = fetchGlobalWidgetRevisions;
          break;
        }

      case FolderAssetTypes.CUSTOM_WIDGET:
        {
          fetchRevisionsAction = fetchCustomWidgetRevisions;
          break;
        }

      case FolderAssetTypes.RAW_ASSET:
        fetchRevisionsAction = fetchRawAssetRevisions;
        break;

      default:
        {
          throw new Error(`Invalid file type to fetch revisions for. Received '${fileType}'.`);
        }
    }

    dispatch(fetchRevisionsAction(fileId, offset));
  };
}
/**
 * Revert files.
 * @param {Number} fileId     ID of file to revert.
 * @param {Revision} revision Revision to revert file to.
 */

export function revertFile(fileId, revision) {
  return (dispatch, getState) => {
    const state = getState();
    const {
      type: fileType
    } = getFile(state, {
      fileId
    });

    switch (fileType) {
      case FolderAssetTypes.TEMPLATE:
      case FolderAssetTypes.CSS:
      case FolderAssetTypes.JS:
        {
          dispatch(revertTemplateBuffers(revision));
          break;
        }

      case FolderAssetTypes.LAYOUT:
      case FolderAssetTypes.GLOBAL_GROUP:
        {
          dispatch(revertUnifiedLayout(revision));
          break;
        }

      case FolderAssetTypes.GLOBAL_WIDGET:
        {
          dispatch(revertGlobalWidget(fileId, revision));
          break;
        }

      case FolderAssetTypes.CUSTOM_WIDGET:
        {
          dispatch(revertCustomWidget(revision));
          break;
        }

      case FolderAssetTypes.RAW_ASSET:
        {
          dispatch(revertRawAsset(revision));
          break;
        }

      default:
        {
          throw new Error(`Invalid file type to revert. Received '${fileType}'.`);
        }
    }
  };
}
/*
 * PUT
 *  - designmanager/v1/layouts
 *  - designmanager/v1/templates
 *  - designmanager/v1/custom-widgets
 */

export function renameFile(fileId, fileName) {
  const {
    request,
    receive,
    error
  } = renameFile;
  return (dispatch, getState) => {
    const state = getState();
    const file = getFile(state, {
      fileId
    });

    if (!file) {
      dispatch(error(new ReferenceError('Unable to find file to rename')));
      return;
    }

    const {
      name: currentFileName,
      type: fileType,
      path: filePath
    } = file;
    const requestData = {};

    const onSaveSuccess = result => {
      dispatch(receive(fileId, filePath, fileName, result, fileType));
    };

    const onSaveError = __error => {
      dispatch(error(fileId, filePath, fileName, currentFileName, __error));
    };

    let saveByPath = false;

    switch (fileType) {
      case FolderAssetTypes.LAYOUT:
      case FolderAssetTypes.GLOBAL_GROUP:
      case FolderAssetTypes.TEMPLATE:
      case FolderAssetTypes.CSS:
      case FolderAssetTypes.JS:
        {
          requestData.label = fileName;
          break;
        }

      case FolderAssetTypes.CUSTOM_WIDGET:
        {
          requestData.name = fileName;
          break;
        }

      case FolderAssetTypes.GLOBAL_WIDGET:
        {
          requestData.label = fileName;
          requestData.name = getGlobalWidgetNameFromLabel(fileName);
          break;
        }

      case FolderAssetTypes.RAW_ASSET:
        {
          const path = filePath.split('/').slice(0, -1).join('/');
          requestData.path = `${path}/${fileName}`;
          saveByPath = true;
          break;
        }

      default:
        break;
    }

    dispatch(request(fileId, filePath, fileName));

    if (saveByPath) {
      finderFilesSaveByPath(fileType, filePath, requestData).then(onSaveSuccess).catch(onSaveError);
    } else {
      finderFilesSave(fileType, fileId, requestData).then(onSaveSuccess).catch(onSaveError);
    }
  };
}

renameFile.request = (fileId, filePath, fileName) => {
  return {
    type: FINDER_FILE_RENAME,
    fileId,
    filePath,
    fileName
  };
};

renameFile.receive = (fileId, filePath, fileName, file, fileType) => {
  return {
    type: FINDER_FILE_RENAME,
    fileId,
    fileName,
    filePath,
    file,
    fileType
  };
};

renameFile.error = (fileId, filePath, fileName, oldFileName, error) => {
  return {
    type: FINDER_FILE_RENAME,
    fileId,
    fileName,
    filePath,
    oldFileName,
    error
  };
};
/**
 * Duplicate files
 * @param  {Number} fileId ID of file to duplicate.
 */


export function duplicateFile(fileId) {
  const {
    request,
    receive,
    error
  } = duplicateFile;
  return (dispatch, getState) => {
    const state = getState();
    const file = getFile(state, {
      fileId
    });
    const duplicateName = getDuplicateFileName(state, {
      fileId
    });
    const fileType = file.type;
    dispatch(request(fileId, duplicateName));
    const requestData = {};
    let useCloneByPath = false;

    switch (fileType) {
      case FolderAssetTypes.LAYOUT:
        {
          requestData.label = duplicateName;
          requestData.folderId = getDuplicateFileFolderId(state, {
            fileId
          });
          break;
        }

      case FolderAssetTypes.TEMPLATE:
        {
          requestData.label = duplicateName;
          useCloneByPath = !!file && !!file.path;
          requestData.folderId = getDuplicateFileFolderId(state, {
            fileId
          });
          break;
        }

      case FolderAssetTypes.CSS:
      case FolderAssetTypes.JS:
        {
          requestData.label = duplicateName;
          useCloneByPath = true;
          break;
        }

      case FolderAssetTypes.CUSTOM_WIDGET:
        {
          useCloneByPath = !!file.path;
          requestData.name = duplicateName;
          requestData.schemaVersion = 2;
          const customWidgetsData = getCustomWidgetsData(state);
          const catalogWidget = customWidgetsData[fileId];

          if (catalogWidget && catalogWidget.schemaVersion === 1) {
            requestData.isAvailableForNewContent = false;
          }

          break;
        }

      case FolderAssetTypes.RAW_ASSET:
        {
          useCloneByPath = true;
          requestData.label = duplicateName;
          break;
        }

      default:
        break;
    }

    const req = useCloneByPath ? finderFilesCloneByPath(fileType, file.path, requestData) : finderFilesClone(fileType, fileId, requestData);
    return req.then(result => {
      dispatch(receive(result, fileType));

      if (useCloneByPath) {
        dispatch(fetchContents(file.folderId));
      }
    }).catch(__error => {
      dispatch(error(fileId, __error));
    });
  };
}

duplicateFile.request = (fileId, duplicateName) => {
  return {
    type: FINDER_FILE_DUPLICATE,
    fileId,
    duplicateName
  };
};

duplicateFile.receive = (file, fileType) => {
  return {
    type: FINDER_FILE_DUPLICATE,
    file,
    fileType
  };
};

duplicateFile.error = (fileId, error) => {
  return {
    type: FINDER_FILE_DUPLICATE,
    fileId,
    error
  };
};
/**
 * Duplicate read only files
 * @param  {Number} fileId ID of file to duplicate.
 * @param  {String} fileType type of file to duplicate.
 * @param  {String} duplicateName name of duplicate file.
 */


export function duplicateReadOnlyFile(fileId, fileType, duplicateName, shouldReplaceNestedModules) {
  const {
    request,
    receive,
    error
  } = duplicateReadOnlyFile;
  return (dispatch, getState) => {
    dispatch(request(fileId, duplicateName));
    const meta = {};
    const state = getState();
    const file = getFile(state, {
      fileId
    });
    const requestData = {};
    let useCloneByPath = false;

    switch (fileType) {
      case FolderAssetTypes.LAYOUT:
      case FolderAssetTypes.TEMPLATE:
        {
          requestData.label = duplicateName;
          useCloneByPath = file && file.assetSource === HOLLOW;
          requestData.folderId = getDuplicateFileFolderId(state, {
            fileId
          });
          break;
        }

      case FolderAssetTypes.CSS:
      case FolderAssetTypes.JS:
        {
          requestData.label = duplicateName;
          break;
        }

      case FolderAssetTypes.CUSTOM_WIDGET:
        {
          useCloneByPath = !!file.path;
          requestData.name = duplicateName;
          requestData.displayName = duplicateName;
          requestData.schemaVersion = 2;
          const customWidgetsData = getCustomWidgetsData(getState());
          const catalogWidget = customWidgetsData[fileId];

          if (catalogWidget && catalogWidget.schemaVersion === 1) {
            requestData.isAvailableForNewContent = false;
          }

          if (file && file.isDefault) {
            meta.path = file.path;
            meta.isDefault = true;
          }

          if (shouldReplaceNestedModules) {
            requestData.shouldReplaceNestedModules = true;
          }

          break;
        }

      case FolderAssetTypes.RAW_ASSET:
        {
          useCloneByPath = true;
          requestData.label = duplicateName;
          break;
        }

      default:
        break;
    }

    const req = useCloneByPath ? finderFilesCloneByPath(fileType, file.path, requestData) : finderFilesClone(fileType, fileId, requestData);
    return req.then(result => {
      dispatch(receive(result, fileType, meta));
    }).catch(__error => {
      dispatch(error(fileId, __error));
    });
  };
}

duplicateReadOnlyFile.request = (fileId, duplicateName) => {
  return {
    type: FINDER_FILE_DUPLICATE_READ_ONLY,
    fileId,
    duplicateName
  };
};

duplicateReadOnlyFile.receive = (file, fileType, meta) => {
  return {
    type: FINDER_FILE_DUPLICATE_READ_ONLY,
    file,
    fileType,
    meta
  };
};

duplicateReadOnlyFile.error = (fileId, error) => {
  return {
    type: FINDER_FILE_DUPLICATE_READ_ONLY,
    fileId,
    error
  };
};
/**
 * Duplicate layout to template file
 * @param {Number} ID of the Layout to clone to template
 */


export function duplicateLayoutToTemplate(fileId) {
  return dispatch => {
    const {
      request,
      receive,
      error
    } = duplicateLayoutToTemplate;
    dispatch(request(fileId));
    layoutCloneToFile(fileId, {}).then(result => {
      dispatch(receive(result));
    }).catch(__error => {
      dispatch(error(fileId, __error));
    });
  };
}

duplicateLayoutToTemplate.request = fileId => {
  return {
    type: FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE,
    fileId
  };
};

duplicateLayoutToTemplate.receive = file => {
  return {
    type: FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE,
    file,
    fileType: FolderAssetTypes.TEMPLATE
  };
};

duplicateLayoutToTemplate.error = (fileId, error) => {
  return {
    type: FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE,
    fileId,
    error
  };
};
/**
 * Restore deleted files
 * @param  {Number} fileId ID of deleted file to restore.
 */


export function restoreDeleted(fileId) {
  const {
    request,
    receive,
    error
  } = restoreDeleted;
  return (dispatch, getState) => {
    const state = getState();
    const file = getFile(state, {
      fileId
    });

    if (!file) {
      return dispatch(error(fileId, null, new ReferenceError(`Unable to find file to restore. fileId = '${fileId}'`)));
    }

    const {
      name: fileName,
      type: fileType
    } = file;
    dispatch(request(fileId));
    const useByModuleIdClient = getIsModuleIdMigrationUngated(state) && fileType === FolderAssetTypes.CUSTOM_WIDGET;

    if (useByModuleIdClient && !file.moduleId) {
      return dispatch(error(fileId, fileName, new ReferenceError(`restoreDeleted: Missing moduleId for file fileId = '${fileId}'`)));
    }

    const req = useByModuleIdClient ? customWidgetsByModuleId.restoreDeleted(file.moduleId) : finderFilesRestoreDeleted(fileType, fileId);
    return req.then(result => {
      dispatch(receive(result, fileType));
    }).catch(__error => {
      dispatch(error(fileId, fileName, __error));
    });
  };
}

restoreDeleted.request = fileId => {
  return {
    type: FINDER_FILE_RESTORE_DELETED,
    fileId
  };
};

restoreDeleted.receive = (file, fileType) => {
  return {
    type: FINDER_FILE_RESTORE_DELETED,
    file,
    fileType
  };
};

restoreDeleted.error = (fileId, fileName, error) => {
  return {
    type: FINDER_FILE_RESTORE_DELETED,
    fileId,
    fileName,
    error
  };
};
/**
 * trackCreatePage
 * This function is for tracking purposes only,
 * pages are create through a redirect in IDE
 * @param {number} assetId
 * @param {string} filetype
 */


export function trackCreatePage(assetId, fileType) {
  return {
    type: FINDER_FILE_CREATE_PAGE,
    assetId,
    fileType
  };
}
export function copyPath(fileId) {
  const {
    success,
    fail
  } = copyPath;
  return (dispatch, getState) => {
    const file = getFile(getState(), {
      fileId
    });

    if (!file) {
      dispatch(fail());
      return;
    }

    const path = file.generatedTemplatePath || file.path;
    const absolutePath = convertToAbsolute(path);
    const text = file.type === FolderAssetTypes.CUSTOM_WIDGET ? `${absolutePath}${MODULE_ASSET_EXTENSION}` : absolutePath;
    copyToClipboard({
      text,
      onSuccess: () => dispatch(success(text)),
      onFail: () => dispatch(fail(text))
    });
  };
}

copyPath.success = path => {
  return {
    type: FINDER_FILE_COPY_PATH,
    copied: true,
    path
  };
};

copyPath.fail = path => {
  return {
    type: FINDER_FILE_COPY_PATH,
    copied: false,
    path
  };
};

export function copyPublicUrl({
  id,
  path
}) {
  const {
    success,
    fail
  } = copyPublicUrl;
  return dispatch => {
    const publicUrl = path ? `{{ get_asset_url('${convertToAbsolute(path)}') }}` : `{{ get_public_template_url_by_id('${id}') }}`;
    copyToClipboard({
      text: publicUrl,
      onSuccess: () => dispatch(success(path || id)),
      onFail: () => dispatch(fail(path || id))
    });
  };
}

copyPublicUrl.success = path => {
  return {
    type: FINDER_FILE_COPY_PUBLIC_URL,
    copied: true,
    path
  };
};

copyPublicUrl.fail = path => {
  return {
    type: FINDER_FILE_COPY_PUBLIC_URL,
    copied: false,
    path
  };
};

export function copyModuleSnippet(fileId) {
  const {
    success,
    fail
  } = copyModuleSnippet;
  return (dispatch, getState) => {
    const file = getFile(getState(), {
      fileId
    });

    if (!file) {
      dispatch(fail());
      return;
    }

    copyToClipboard({
      text: getModuleSnippet(file),
      onSuccess: () => dispatch(success()),
      onFail: () => dispatch(fail())
    });
  };
}

copyModuleSnippet.success = () => {
  return {
    type: FINDER_FILE_COPY_MODULE_SNIPPET,
    copied: true
  };
};

copyModuleSnippet.fail = () => {
  return {
    type: FINDER_FILE_COPY_MODULE_SNIPPET,
    copied: false
  };
};

export function viewLayoutSource(layoutId) {
  return {
    type: FINDER_FILE_VIEW_LAYOUT_SOURCE,
    layoutId
  };
}
export const updateFileUploadProgress = (fileName, fullPath, folderId, progress) => ({
  type: FINDER_FILE_UPLOAD_UPDATE_PROGRESS,
  fileName,
  fullPath,
  folderId,
  progress
});
export const updateFileUploadFinished = (fileName, fullPath, folderId) => ({
  type: FINDER_FILE_UPLOAD_FINISHED,
  fileName,
  fullPath,
  folderId
});
export const fileUploadValidation = (error, file) => ({
  file,
  type: FINDER_FILE_UPLOAD,
  error
});
/**
 * Upload files to Design Manager
 * @param {string} fullPath
 * @param {object} file
 * @param {number} folderId
 *
 */

export const uploadSingleFile = ({
  file,
  folder
}) => {
  return (dispatch, getState) => {
    const {
      error
    } = uploadSingleFile;
    const state = getState();
    let fullPath;
    let folderId;

    if (folder) {
      fullPath = folder.fullPath;
      folderId = folder.id;
    } else {
      fullPath = '';
      folderId = getRootFolderId(state);
    }

    const handleProgress = e => {
      const progress = e.loaded / e.total * 100;
      dispatch(updateFileUploadProgress(file.name, fullPath, folderId, progress));
    };

    return uploadFile(fullPath, file, handleProgress).then(() => {
      dispatch(fetchContents(folderId)).then(() => dispatch(updateFileUploadFinished(file.name, fullPath, folderId)));
    }).catch(__error => {
      dispatch(error(__error, file));
      dispatch(updateFileUploadFinished(file.name, fullPath, folderId));
    });
  };
};

uploadSingleFile.error = (error, file) => {
  return {
    file,
    type: FINDER_FILE_UPLOAD,
    error
  };
};

export const uploadFiles = (fileList, folder) => {
  return (dispatch, getState) => {
    if (!fileList || fileList.length === 0) {
      return Promise.resolve([]);
    }

    const fileMap = fileList.map(file => uploadSingleFile({
      file,
      folder
    })(dispatch, getState));
    return Promise.all(fileMap);
  };
};
export const validateFileUpload = file => {
  return dispatch => {
    if (!validateFilenameCharacters(file.name, RAW_ASSET)) {
      dispatch(fileUploadValidation({
        errorType: FILENAME_CONTAINS_INVALID_CHAR
      }, file));
      return false;
    }

    return true;
  };
};